import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Download, FileSpreadsheet, Play, RotateCcw, Trash2, TriangleAlert, Upload } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import {
  DELIMITER_LABELS,
  DUPLICATE_REASON_LABELS,
  formatDateTime,
  formatNumber,
  hasPermission,
  importRowAddress,
  importRowName,
  importStep,
  labelOf,
  mappingProblems,
  PARTY_IMPORT_DECISION_LABELS,
  PARTY_IMPORT_RESULT_LABELS,
  PARTY_IMPORT_ROW_FILTERS,
  PARTY_IMPORT_STATUS,
  PARTY_SOURCE_LABELS,
} from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FileDropzone,
  Label,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { useListState } from '../../lib/list-search.js';

const ACCEPT = '.csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const toOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

const breadcrumb = (
  <Link to="/personen" className="hover:underline">
    Personen
  </Link>
);

/** Status badge of an import. @param {{ status: string }} props */
export function ImportStatusBadge({ status }) {
  const meta = PARTY_IMPORT_STATUS[status] ?? { label: status, variant: 'neutral' };
  return <Badge variant={/** @type {any} */ (meta.variant)}>{meta.label}</Badge>;
}

/** Personen › Importieren (F-104): Datei hochladen und frühere Importe. */
export function PartyImportsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const imports = useQuery(adminQueries.partyImports(api, list.apiParams));
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [progress, setProgress] = useState(0);
  const upload = useMutation({
    mutationFn: (/** @type {File} */ file) => api.admin.partyImports.upload(file, { onProgress: setProgress }),
    onSuccess: (body) => navigate({ to: '/personen/import/$importId', params: { importId: String(body.data.id) } }),
  });

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title="Personen importieren"
        description="Personen und Adressen aus einer CSV- oder Excel-Datei übernehmen – mit Spaltenzuordnung, Prüfung, Dublettenabgleich und Entscheidung je Zeile."
      />
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Datei hochladen</CardTitle>
            <CardDescription>CSV (UTF-8 oder Windows-1252, Trennzeichen wird erkannt) oder XLSX (erstes Tabellenblatt), höchstens 10 MB.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <FileDropzone files={files} onFilesChange={setFiles} accept={ACCEPT} multiple={false} label="Importdatei" />
            {upload.error && (
              <Alert variant="destructive">
                <TriangleAlert aria-hidden="true" />
                <AlertTitle>Datei nicht übernommen</AlertTitle>
                <AlertDescription>{getErrorMessage(upload.error, 'Die Datei konnte nicht gelesen werden.')}</AlertDescription>
              </Alert>
            )}
            <div className="flex items-center gap-3">
              <Button disabled={files.length === 0} loading={upload.isPending} onClick={() => upload.mutate(files[0])}>
                <Upload aria-hidden="true" /> Hochladen und Spalten zuordnen
              </Button>
              {upload.isPending && progress > 0 && <span className="text-sm text-muted-foreground numeric">{Math.round(progress * 100)} %</span>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">So funktioniert der Import</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
              <li>Spalten den Feldern zuordnen – bekannte Spaltenköpfe (z. B. „Vorname“, „PLZ“, „E-Mail“, Altportal-Export) werden vorbelegt.</li>
              <li>Vorschau prüfen: Fehler, Hinweise und mögliche Dubletten (gleiche E-Mail oder gleicher Name + PLZ).</li>
              <li>Je Zeile entscheiden: neu anlegen, bestehende Person aktualisieren oder überspringen.</li>
              <li>Import ausführen – er läuft im Hintergrund; danach steht ein Ergebnisbericht (CSV) bereit.</li>
            </ol>
          </CardContent>
        </Card>
      </div>

      <h2 className="mt-6 mb-2 text-base font-semibold">Bisherige Importe</h2>
      {imports.error ? (
        <ErrorState error={imports.error} onRetry={() => imports.refetch()} />
      ) : (
        <DataTable
          label="Bisherige Importe"
          columns={importColumns}
          data={imports.data?.data}
          rowCount={imports.data?.meta?.total}
          isLoading={imports.isPending}
          isFetching={imports.isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Noch keine Importe"
        />
      )}
    </>
  );
}

const importColumns = [
  {
    accessorKey: 'file_name',
    header: 'Datei',
    cell: ({ row }) => (
      <Link to="/personen/import/$importId" params={{ importId: String(row.original.id) }} className="font-medium text-primary hover:underline">
        {row.original.file_name}
      </Link>
    ),
  },
  { id: 'status', header: 'Status', cell: ({ row }) => <ImportStatusBadge status={row.original.status} /> },
  {
    id: 'stats',
    header: 'Zeilen',
    cell: ({ row }) => {
      const s = row.original.stats ?? {};
      return row.original.status === 'completed' ? (
        <span className="text-sm">
          {formatNumber(s.created ?? 0)} angelegt · {formatNumber(s.updated ?? 0)} aktualisiert · {formatNumber(s.skipped ?? 0)} übersprungen
        </span>
      ) : (
        <span className="numeric">{formatNumber(s.rows ?? 0)}</span>
      );
    },
  },
  { id: 'created_by', header: 'Von', cell: ({ row }) => row.original.created_by ?? '–' },
  { id: 'created_at', header: 'Hochgeladen', cell: ({ row }) => <span className="numeric">{formatDateTime(row.original.created_at)}</span> },
];

/** Personen › Import #id: Zuordnung → Vorschau/Entscheidungen → Ergebnis. */
export function PartyImportDetailPage() {
  const api = useApi();
  const { importId } = useParams({ strict: false });
  const query = useQuery(adminQueries.partyImport(api, importId));
  const [remapping, setRemapping] = useState(false);

  if (query.error) {
    return (
      <>
        <PageHeader breadcrumb={breadcrumb} title="Personen-Import" />
        <ErrorState error={query.error} onRetry={() => query.refetch()} notFoundTitle="Import nicht gefunden" />
      </>
    );
  }
  if (query.isPending) {
    return (
      <>
        <PageHeader breadcrumb={breadcrumb} title="Personen-Import" />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  const imp = query.data.data;
  const meta = query.data.meta;
  const step = remapping && imp.status === 'previewed' ? 'mapping' : importStep(imp.status);

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`Import: ${imp.file_name}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <ImportStatusBadge status={imp.status} />
            <span className="numeric">{formatNumber(imp.stats?.rows ?? 0)} Datenzeilen</span>
            {imp.format === 'csv' && (
              <span>
                {imp.encoding} · {labelOf(DELIMITER_LABELS, imp.delimiter)}
              </span>
            )}
          </span>
        }
        actions={<DiscardButton imp={imp} />}
      />
      <Stepper
        label="Ablauf des Imports"
        className="mb-4"
        current={step}
        steps={[
          { id: 'mapping', label: 'Spalten zuordnen', done: step !== 'mapping' },
          { id: 'preview', label: 'Prüfen & entscheiden', done: step === 'result' },
          { id: 'result', label: 'Ergebnis', done: imp.status === 'completed' },
        ]}
      />
      {imp.previous_imports?.length > 0 && step !== 'result' && (
        <Alert variant="warning" className="mb-3">
          <TriangleAlert aria-hidden="true" />
          <AlertTitle>Diese Datei wurde bereits importiert</AlertTitle>
          <AlertDescription>
            Gleiche Datei wie Import{' '}
            {imp.previous_imports.map((p, i) => (
              <span key={p.id}>
                {i > 0 && ', '}
                <Link to="/personen/import/$importId" params={{ importId: String(p.id) }} className="underline">
                  #{p.id}
                </Link>{' '}
                vom {formatDateTime(p.created_at)}
              </span>
            ))}
            . Bereits vorhandene Personen werden als Dubletten erkannt und zum Aktualisieren bzw. Prüfen vorgeschlagen.
          </AlertDescription>
        </Alert>
      )}
      {step === 'mapping' && (
        <MappingStep imp={imp} meta={meta} onDone={() => setRemapping(false)} onCancel={remapping ? () => setRemapping(false) : undefined} />
      )}
      {step === 'preview' && <PreviewStep imp={imp} onRemap={() => setRemapping(true)} />}
      {step === 'result' && <ResultStep imp={imp} />}
    </>
  );
}

function DiscardButton({ imp }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  if (imp.status === 'queued' || imp.status === 'processing') return null;
  const done = imp.status === 'completed';
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Trash2 aria-hidden="true" /> {done ? 'Protokoll löschen' : 'Verwerfen'}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={done ? 'Importprotokoll löschen?' : 'Import verwerfen?'}
        description={
          done
            ? 'Vorschau und Ergebnisbericht werden gelöscht. Angelegte bzw. aktualisierte Personen bleiben erhalten.'
            : 'Die hochgeladene Datei und die Vorschau werden gelöscht. Es wurden noch keine Personen angelegt.'
        }
        confirmLabel={done ? 'Löschen' : 'Verwerfen'}
        destructive
        onConfirm={async () => {
          try {
            await api.admin.partyImports.remove(imp.id);
            await queryClient.invalidateQueries({ queryKey: keys.admin.resource('party-imports') });
            toast.success(done ? 'Protokoll gelöscht' : 'Import verworfen');
            navigate({ to: '/personen/import' });
          } catch (e) {
            toast.error(getErrorMessage(e, 'Der Import konnte nicht gelöscht werden.'));
          }
        }}
      />
    </>
  );
}

/** Step 1: column → field mapping with sample values, header/encoding/delimiter options. */
function MappingStep({ imp, meta, onDone, onCancel }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [mapping, setMapping] = useState(() => ({ ...(imp.mapping ?? {}) }));
  const [hasHeader, setHasHeader] = useState(Boolean(imp.has_header));
  const [encoding, setEncoding] = useState(imp.encoding ?? '');
  const [delimiter, setDelimiter] = useState(imp.delimiter ?? '');
  const problems = mappingProblems(mapping);
  const sample = imp.sample ?? [];
  const dataRows = hasHeader ? sample.slice(1, 4) : sample.slice(0, 3);
  const fieldOptions = meta.fields ?? [];

  const apply = useMutation({
    mutationFn: () =>
      api.admin.partyImports.applyMapping(imp.id, {
        mapping: Object.fromEntries(Object.entries(mapping).filter(([, f]) => f)),
        has_header: hasHeader,
        ...(imp.format === 'csv' ? { encoding: encoding || null, delimiter: delimiter || null } : {}),
      }),
    onSuccess: (body) => {
      queryClient.setQueryData(keys.admin.detail('party-imports', imp.id), body);
      queryClient.invalidateQueries({ queryKey: keys.admin.sub('party-imports', imp.id, 'rows') });
      queryClient.invalidateQueries({ queryKey: keys.admin.list('party-imports') });
      toast.success('Vorschau erstellt');
      onDone();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Spalten zuordnen</CardTitle>
        <CardDescription>
          Ordnen Sie jeder Spalte ein Feld zu. Nicht zugeordnete Spalten werden nicht importiert. Personengruppen/Tags werden nicht unterstützt.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} /> Erste Zeile enthält Spaltenköpfe
          </label>
          {imp.format === 'csv' && (
            <>
              <div className="flex flex-col gap-1">
                <Label htmlFor="import-encoding">Zeichensatz</Label>
                <Select id="import-encoding" size="sm" value={encoding} onChange={(e) => setEncoding(e.target.value)}>
                  {(meta.encodings ?? []).map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="import-delimiter">Trennzeichen</Label>
                <Select id="import-delimiter" size="sm" value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
                  {(meta.delimiters ?? []).map((d) => (
                    <option key={d} value={d}>
                      {labelOf(DELIMITER_LABELS, d)}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          )}
        </div>
        {(encoding !== (imp.encoding ?? '') || delimiter !== (imp.delimiter ?? '')) && (
          <p className="text-xs text-muted-foreground">Die Beispielwerte zeigen noch die erkannte Einstellung; die Vorschau liest die Datei neu ein.</p>
        )}
        <div className="overflow-x-auto rounded-md border">
          <Table aria-label="Spaltenzuordnung">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Spalte in der Datei</TableHead>
                <TableHead>Beispielwerte</TableHead>
                <TableHead className="w-64">Feld</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(imp.columns ?? []).map((column, index) => (
                <TableRow key={index}>
                  <TableCell className="numeric text-muted-foreground">{index + 1}</TableCell>
                  <TableCell className="font-medium">
                    {hasHeader ? column || <em className="text-muted-foreground">ohne Titel</em> : `Spalte ${index + 1}`}
                  </TableCell>
                  <TableCell className="max-w-72 text-sm text-muted-foreground">
                    <span className="line-clamp-2 break-all">
                      {dataRows
                        .map((r) => r.cells[index])
                        .filter(Boolean)
                        .join(' · ') || '–'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="sm"
                      aria-label={`Feld für Spalte ${index + 1}${hasHeader && column ? ` (${column})` : ''}`}
                      value={mapping[index] ?? ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [index]: e.target.value || null }))}
                    >
                      <option value="">— nicht importieren —</option>
                      {fieldOptions.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {problems.length > 0 && (
          <Alert variant="warning">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>{problems.join(' ')}</AlertDescription>
          </Alert>
        )}
        {apply.error && (
          <Alert variant="destructive">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>{getErrorMessage(apply.error, 'Die Vorschau konnte nicht erstellt werden.')}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          <Button disabled={problems.length > 0} loading={apply.isPending} onClick={() => apply.mutate()}>
            Vorschau erstellen
          </Button>
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Abbrechen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsGrid({ stats, result = false }) {
  const s = stats ?? {};
  const items = result
    ? [
        ['Angelegt', s.created],
        ['Aktualisiert', s.updated],
        ['Übersprungen', s.skipped],
        ['Fehlgeschlagen', s.failed],
      ]
    : [
        ['Datenzeilen', s.rows],
        ['Mit Fehlern', s.errors],
        ['Mögliche Dubletten', s.duplicates],
        ['Neu anlegen', s.to_create],
        ['Aktualisieren', s.to_update],
        ['Überspringen', s.to_skip],
      ];
  return (
    <div className={result ? 'grid grid-cols-2 gap-3 lg:grid-cols-4' : 'grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6'}>
      {items.map(([label, value]) => (
        <StatCard key={label} label={label} value={formatNumber(value ?? 0)} />
      ))}
    </div>
  );
}

/** Step 2: preview with per-row decisions, then execute. */
function PreviewStep({ imp, onRemap }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const execute = useMutation({
    mutationFn: () => api.admin.partyImports.execute(imp.id),
    onSuccess: (body) => {
      queryClient.setQueryData(keys.admin.detail('party-imports', imp.id), body);
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('party-imports') });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('parties') });
      toast.success('Import gestartet');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Der Import konnte nicht gestartet werden.')),
  });
  const s = imp.stats ?? {};
  const changes = (s.to_create ?? 0) + (s.to_update ?? 0);

  return (
    <div className="flex flex-col gap-4">
      <StatsGrid stats={imp.stats} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setConfirm(true)} disabled={changes === 0} loading={execute.isPending}>
          <Play aria-hidden="true" /> Import ausführen
        </Button>
        <Button variant="outline" onClick={onRemap}>
          <RotateCcw aria-hidden="true" /> Zuordnung ändern
        </Button>
      </div>
      <RowsTable imp={imp} editable />
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Import ausführen?"
        description={`${formatNumber(s.to_create ?? 0)} Person(en) werden neu angelegt, ${formatNumber(s.to_update ?? 0)} bestehende ergänzt, ${formatNumber(
          s.to_skip ?? 0,
        )} Zeile(n) übersprungen. Danach sind keine Änderungen an der Vorschau mehr möglich.`}
        confirmLabel="Ausführen"
        onConfirm={() => execute.mutateAsync()}
      />
    </div>
  );
}

/** Step 3: queued/processing progress or the result with report download. */
function ResultStep({ imp }) {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.admin.partyImports.execute(imp.id),
    onSuccess: (body) => queryClient.setQueryData(keys.admin.detail('party-imports', imp.id), body),
    onError: (e) => toast.error(getErrorMessage(e, 'Der Import konnte nicht wiederholt werden.')),
  });
  const running = imp.status === 'queued' || imp.status === 'processing';

  return (
    <div className="flex flex-col gap-4">
      {running ? (
        <Alert variant="info" role="status">
          <FileSpreadsheet aria-hidden="true" />
          <AlertTitle>{imp.status === 'queued' ? 'Import wartet auf Verarbeitung' : 'Import läuft'}</AlertTitle>
          <AlertDescription>Die Seite aktualisiert sich automatisch. Sie können sie auch verlassen – der Import läuft im Hintergrund weiter.</AlertDescription>
        </Alert>
      ) : imp.status === 'failed' ? (
        <Alert variant="destructive">
          <TriangleAlert aria-hidden="true" />
          <AlertTitle>Import abgebrochen</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            <span>Bereits übernommene Zeilen bleiben erhalten und werden bei einer Wiederholung erkannt.</span>
            <Button size="sm" variant="outline" loading={retry.isPending} onClick={() => retry.mutate()}>
              <RotateCcw aria-hidden="true" /> Wiederholen
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      <StatsGrid stats={imp.stats} result />
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <a href={api.admin.partyImports.reportUrl(imp.id)} download>
            <Download aria-hidden="true" /> Ergebnisbericht (CSV)
          </a>
        </Button>
        {imp.import_run_id && hasPermission(me, 'import-runs.view') && (
          <Button asChild variant="ghost" size="sm">
            <Link to="/administration/importe" search={{ source: 'csv' }}>
              Im Importprotokoll ansehen
            </Link>
          </Button>
        )}
      </div>
      {!running && <RowsTable imp={imp} />}
    </div>
  );
}

/** Rows of the preview/result (server-side filter + pagination). */
function RowsTable({ imp, editable = false }) {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = { ...list.apiParams, per_page: list.apiParams.per_page ?? 50, filter: { status: search.status } };
  const rows = useQuery(adminQueries.partyImportRows(api, imp.id, params));
  const columns = useMemo(() => rowColumns(imp.id, editable), [imp.id, editable]);

  return (
    <div className="flex flex-col gap-2">
      <FilterBar>
        <FilterSelect label="Zeilen" value={search.status} onChange={(v) => list.setFilter('status', v)} options={toOptions(PARTY_IMPORT_ROW_FILTERS)} />
      </FilterBar>
      {rows.error ? (
        <ErrorState error={rows.error} onRetry={() => rows.refetch()} />
      ) : (
        <DataTable
          label="Importzeilen"
          columns={columns}
          data={rows.data?.data}
          rowCount={rows.data?.meta?.total}
          isLoading={rows.isPending}
          isFetching={rows.isFetching}
          getRowId={(row) => String(row.id)}
          pagination={{ ...list.pagination, pageSize: params.per_page }}
          onPaginationChange={list.onPaginationChange}
          pageSizeOptions={[50, 100]}
          emptyState={<EmptyState title="Keine Zeilen" description="Für diesen Filter gibt es keine Zeilen." />}
        />
      )}
    </div>
  );
}

function PartyMini({ party }) {
  if (!party) return null;
  return (
    <span className="flex flex-col">
      <Link to="/personen/$partyId" params={{ partyId: String(party.id) }} className="font-medium text-primary hover:underline" target="_blank">
        {party.display_name || `Person #${party.id}`}
      </Link>
      <span className="text-xs text-muted-foreground">
        {[party.email, party.address, party.source && party.source !== 'manual' ? labelOf(PARTY_SOURCE_LABELS, party.source) : null]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </span>
  );
}

function rowColumns(importId, editable) {
  return [
    { id: 'row_number', header: 'Zeile', cell: ({ row }) => <span className="numeric text-muted-foreground">{row.original.row_number}</span> },
    {
      id: 'data',
      header: 'Daten aus der Datei',
      cell: ({ row }) => {
        const v = row.original.values ?? {};
        return (
          <span className="flex min-w-48 flex-col">
            <span className="font-medium">{importRowName(v) || <em className="text-muted-foreground">ohne Namen</em>}</span>
            <span className="text-xs text-muted-foreground">{[importRowAddress(v), ...(v.emails ?? [])].filter(Boolean).join(' · ')}</span>
          </span>
        );
      },
    },
    {
      id: 'checks',
      header: 'Prüfung',
      cell: ({ row }) => {
        const { errors = [], warnings = [] } = row.original;
        if (errors.length === 0 && warnings.length === 0) return <Badge variant="success">OK</Badge>;
        return (
          <ul className="flex min-w-48 flex-col gap-0.5 text-xs">
            {errors.map((e) => (
              <li key={e} className="text-destructive">
                {e}
              </li>
            ))}
            {warnings.map((w) => (
              <li key={w} className="text-warning-subtle-foreground">
                {w}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      id: 'candidates',
      header: 'Mögliche Dubletten',
      cell: ({ row }) =>
        row.original.candidates.length === 0 ? (
          <span className="text-muted-foreground">–</span>
        ) : (
          <ul className="flex min-w-48 flex-col gap-1.5">
            {row.original.candidates.map((c) => (
              <li key={c.party?.id} className="flex flex-col gap-0.5">
                <PartyMini party={c.party} />
                <span className="flex flex-wrap gap-1">
                  {c.reasons.map((r) => (
                    <Badge key={r} variant={r === 'email' ? 'warning' : 'neutral'}>
                      {labelOf(DUPLICATE_REASON_LABELS, r)}
                    </Badge>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ),
    },
    editable
      ? { id: 'decision', header: 'Entscheidung', cell: ({ row }) => <DecisionCell importId={importId} row={row.original} /> }
      : {
          id: 'result',
          header: 'Ergebnis',
          cell: ({ row }) => (
            <span className="flex min-w-40 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge variant={row.original.result === 'failed' ? 'destructive' : row.original.result === 'skipped' ? 'neutral' : 'success'}>
                  {labelOf(PARTY_IMPORT_RESULT_LABELS, row.original.result) || labelOf(PARTY_IMPORT_DECISION_LABELS, row.original.decision)}
                </Badge>
              </span>
              <PartyMini party={row.original.result_party} />
              {row.original.result_message && <span className="text-xs text-muted-foreground">{row.original.result_message}</span>}
            </span>
          ),
        },
  ];
}

/** Per-row decision (create/update/skip, update target among the candidates). */
function DecisionCell({ importId, row }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const decide = useMutation({
    mutationFn: (/** @type {{ decision: string, target_party_id?: number|null }} */ payload) => api.admin.partyImports.decide(importId, row.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.admin.detail('party-imports', importId) });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Die Entscheidung konnte nicht gespeichert werden.')),
  });
  const hasErrors = row.errors.length > 0;
  const targets = row.candidates.map((c) => c.party).filter(Boolean);
  if (row.target && !targets.some((t) => t.id === row.target.id)) targets.unshift(row.target);
  const value = row.decision === 'update' && row.target ? `update:${row.target.id}` : row.decision;

  return (
    <Select
      size="sm"
      aria-label={`Entscheidung für Zeile ${row.row_number}`}
      value={value}
      disabled={decide.isPending}
      onChange={(e) => {
        const [decision, target] = e.target.value.split(':');
        decide.mutate({ decision, target_party_id: target ? Number(target) : null });
      }}
      wrapperClassName="min-w-48"
    >
      <option value="create" disabled={hasErrors}>
        {PARTY_IMPORT_DECISION_LABELS.create}
      </option>
      {targets.map((t) => (
        <option key={t.id} value={`update:${t.id}`} disabled={hasErrors}>
          Aktualisieren: {t.display_name || `#${t.id}`}
        </option>
      ))}
      <option value="skip">{PARTY_IMPORT_DECISION_LABELS.skip}</option>
    </Select>
  );
}
